/**
 * Shared sync-message log.
 *
 * Both transports push into this ring buffer so the devtools Messages tab
 * is a SINGLE unified timeline of everything flowing between the client
 * and any Durable Object.
 *
 *   UserDO  (raw PartySocket, JSON messages) → pushSyncMessage(…)
 *   NotesDO (Yjs binary CRDT updates)        → pushSyncMessage(…)
 *
 * Entries carry a `source` so UI can render per-DO badges / filters. Size
 * is in bytes — for UserDO we measure `JSON.stringify(msg).length`; for
 * NotesDO we use `update.byteLength` of the Yjs binary delta. Both are
 * good rough approximations of on-the-wire traffic.
 *
 * Lives as a module-level singleton on purpose: messages are generated
 * from non-React code (WebSocket callbacks, Yjs observers) and subscribed
 * to by React components. The buffer survives component remounts so the
 * Messages tab isn't wiped every time you open it.
 */

import { useSyncExternalStore } from 'react';

/**
 * Which Durable Object a message belongs to.
 *
 * `notes-do` is legacy — it was consolidated into UserDO so every
 * Yjs update from the user's root doc now logs as `user-do`. The
 * literal is kept in the union so any in-memory log entries from an
 * old session don't fail type-checks; the devtools treats them as
 * `user-do` in the UI.
 */
export type SyncMessageSource = 'user-do' | 'notes-do' | 'study-group-do';

/**
 * - "out": something the client sent (UserDO JSON push, local Yjs edit
 *   that will be flushed to the server).
 * - "in":  something the server sent (UserDO response, remote Yjs update).
 * - "idb": replayed from local IndexedDB persistence on startup — local
 *   only, no wire traffic, but useful to see when debugging the sync
 *   flow.
 */
export type SyncMessageDirection = 'out' | 'in' | 'idb';

export type SyncMessageLogEntry = {
  source: SyncMessageSource;
  direction: SyncMessageDirection;
  /** Message type for the UI — e.g. "sync-init", "card-push", "yjs-update". */
  type: string;
  /** Epoch ms. */
  ts: number;
  /** Approximate on-the-wire size of the message payload. */
  sizeBytes: number;
  /** Human-readable detail shown in the collapsed row (e.g. deck name). */
  detail?: string;
  /** Parsed JSON payload (UserDO only — Yjs deltas have no JSON body). */
  payload?: Record<string, unknown>;
};

// ---------------------------------------------------------------------------
// Ring buffer
// ---------------------------------------------------------------------------

const MAX_ENTRIES = 100;

// The log exists for the devtools Messages tab. Recording is on by default so
// web devtools keep working unchanged; a platform with no reader (native)
// turns it off at sync-engine init. When off, `pushSyncMessage` is a no-op AND
// the producers skip building the entry — the two `JSON.parse`es per frame in
// `use-do-sync.ts` and the retention of up to MAX_ENTRIES full payloads (a
// cold-start diff plus 99 bulk frames) were pure cost on mobile.
let enabled = true;

/** Turn recording on/off for the session. Off ⇒ pushes are dropped. */
export function setSyncMessageLogEnabled(on: boolean): void {
  enabled = on;
}

/** Producers check this BEFORE building an entry (before any JSON.parse). */
export function isSyncMessageLogEnabled(): boolean {
  return enabled;
}

let buffer: SyncMessageLogEntry[] = [];
const listeners = new Set<() => void>();
const newMessageListeners = new Set<(entry: SyncMessageLogEntry) => void>();

// Snapshot cache — useSyncExternalStore calls getSnapshot eagerly and
// requires identity stability across calls when nothing changed. We bump
// `version` on every push and only reallocate the snapshot array then.
let version = 0;
let cachedSnapshot: SyncMessageLogEntry[] = [];
let cachedSnapshotVersion = -1;

function notify() {
  for (const listener of listeners) listener();
}

/** Push a new entry. Thread-safe in the JS sense (no cross-thread); safe
 *  to call from WebSocket onmessage, Yjs observers, or any sync callback. */
export function pushSyncMessage(entry: SyncMessageLogEntry): void {
  if (!enabled) return;
  buffer.push(entry);
  if (buffer.length > MAX_ENTRIES) {
    buffer = buffer.slice(buffer.length - MAX_ENTRIES);
  }
  version++;
  notify();
  for (const cb of newMessageListeners) cb(entry);
}

/**
 * Subscribe to per-message pushes. Unlike the snapshot-based React hook,
 * this fires exactly once per `pushSyncMessage` with the new entry —
 * `clearSyncMessageLog` does NOT trigger it. Used by the devtools floater
 * overlay to spawn one transient indicator per arriving/sent message.
 */
export function subscribeToNewSyncMessages(
  cb: (entry: SyncMessageLogEntry) => void,
): () => void {
  newMessageListeners.add(cb);
  return () => {
    newMessageListeners.delete(cb);
  };
}

/** Clear the whole log. Used by the "clear" button in devtools. */
export function clearSyncMessageLog(): void {
  if (buffer.length === 0) return;
  buffer = [];
  version++;
  notify();
}

function subscribe(cb: () => void): () => void {
  listeners.add(cb);
  return () => {
    listeners.delete(cb);
  };
}

function getSnapshot(): SyncMessageLogEntry[] {
  if (cachedSnapshotVersion !== version) {
    cachedSnapshot = buffer.slice();
    cachedSnapshotVersion = version;
  }
  return cachedSnapshot;
}

/**
 * React hook — subscribes to the shared log. Returns a snapshot that only
 * changes reference when the buffer actually mutated.
 */
export function useSyncMessageLog(): SyncMessageLogEntry[] {
  return useSyncExternalStore(subscribe, getSnapshot, getSnapshot);
}
